import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'
import { sendHTTPRequest } from '@/util/http'
import { toolkitUrls } from '@/util/links'

export type BlogPost = {
  id: string
  fields: {
    title: string
    url: string
  }
}

export type Webinar = {
  title: string
  date: string
  url: string
}

const BLOG_POSTS_PROFILE = 'common-core'
const WEBINAR_SCHEDULE_TIME_ZONE = 'America/Los_Angeles'
const WEBINAR_DATE_FORMAT = 'EEEE, MMMM d, h:mmaaa zzz'

function viewerTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

function withoutAirtableHeaderRow(records: BlogPost[]): BlogPost[] {
  return records.slice(1)
}

/** The toolkit server answers a failed Airtable fetch with a 200 and an error object, which Vue 2 showed as no content. */
async function jsonList<T>(response: Response): Promise<T[]> {
  const payload = await response.json()
  if (!Array.isArray(payload)) {
    return []
  }
  return payload
}

/** Webinar times are scheduled in Pacific wall-clock time; the offset the server appends is not the real one. */
const FRACTIONAL_SECONDS_AND_OFFSET = /(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/

function pacificWallClockTime(date: string): string {
  return date.replace(FRACTIONAL_SECONDS_AND_OFFSET, '')
}

export function formatWebinarDate(date: string, timeZone = viewerTimeZone()): string {
  const startsAt = fromZonedTime(pacificWallClockTime(date), WEBINAR_SCHEDULE_TIME_ZONE)
  return formatInTimeZone(startsAt, timeZone, WEBINAR_DATE_FORMAT)
}

export async function getBlogPosts(): Promise<BlogPost[]> {
  const response = await sendHTTPRequest({ url: `${toolkitUrls.blogPosts}/${BLOG_POSTS_PROFILE}` })
  const records = await jsonList<BlogPost>(response)
  return withoutAirtableHeaderRow(records)
}

export async function getUpcomingWebinars(): Promise<Webinar[]> {
  const response = await sendHTTPRequest({ url: toolkitUrls.upcomingWebinars })
  const webinars = await jsonList<Webinar>(response)
  return webinars.map(webinar => ({ ...webinar, date: formatWebinarDate(webinar.date) }))
}
